import {Component, Children, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import injectSheet from 'react-jss';

import {important, lighten, rem, size, truncateText, userSelect} from '-/styles/utils';

const styles = theme => ({
    root: {
        ...size('auto', '47px'),
        ...userSelect('none'),
        position: 'relative',
        padding: `0 ${rem(18)}`,
        border: 'none',
        borderLeft: '1px solid gainsboro',
        borderRight: '1px solid gainsboro',
        fontFamily: 'inherit',
        fontSize: rem(14),
        fontWeight: 'normal',
        lineHeight: 47,
        color: '#000',
        cursor: 'pointer',
        outline: 'none',
        whiteSpace: 'nowrap',
        backgroundColor: '#fff',
        WebkitTapHighlightColor: 'transparent',

        '&:focus': {
            border: '1px solid #999',
            boxShadow: '0 0 2px rgba(73, 143, 225, 0.7)'
        },

        '$:active': {
            borderColor: '#999'
        },

        '&::after, &:after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            right: rem(12),
            top: '50%',
            width: rem(5),
            height: rem(5),
            marginTop: rem(-4),
            borderBottom: '2px solid #999',
            borderRight: '2px solid #999',
            transformOrigin: '66% 66%',
            transform: 'rotate(45deg)',
            transition: 'all 0.15s ease-in-out',
            pointerEvents: 'none'
        },

        '&:hover': {
            borderColor: '#dbdbdb'
        }
    },
    current: {
        ...truncateText('100%'),
        width: '100%',
        fontFamily: theme.fontFamily,
        fontSize: rem(14),
        fontWeight: 'normal',
        lineHeight: rem(47),
        opacity: .8,
        '&.placeholder': {
            color: lighten(theme.colors.strong, .4)
        },
        '&:focus': {
            outline: 'none'
        }
    },
    hide: {},
    select: {
        position: 'absolute',
        width: '100%',
        top: 0,
        left: 0,
        '&:focus': {
            border: important('none'),
            boxShadow: important('none')
        },
        '&$hide': {
            opacity: important(0)
        },
        '&:not($hide)': {
            opacity: important(1)
        }
    },
    selected: {
        border: important('1px solid #4a90e2')
    },
    disabled: {
        borderColor: '#ededed',
        color: '#999',
        pointerEvents: 'none',
        '&::after, &:after': {
            borderColor: '#cccccc'
        }
    },
    wide: {
        width: '100%',
        '&$list': {
            left: 0,
            right: 0
        }
    },
    small: {
        height: 36,
        fontSize: rem(12),
        lineHeight: rem(34),

        '& $option': {
            minHeight: 34,
            lineHeight: rem(34)
        },
        '&::after, &:after': {
            width: rem(4),
            height: rem(4)
        }
    },
    open: {
        border: '1px solid #999 !important',
        boxShadow: '0 0 2px rgba(73, 143, 225, 0.7)',

        '& $list': {
            opacity: 1,
            pointerEvents: 'auto',
            transform: 'scale(1) translateY(0)'
        },
        '&::after, &:after': {
            transform: 'rotate(-135deg)'
        }
    },
    list: {
        boxSizing: 'border-box',
        position: 'absolute',
        top: '103%',
        left: 0,
        width: '100%',
        padding: `0 ${rem(10)}`,
        borderBottomLeftRadius: 5,
        borderBottomRightRadius: 5,
        backgroundColor: '#fff',
        pointerEvents: 'none',
        opacity: 0,
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(68, 68, 68, 0.11)',
        transformOrigin: '50% 0',
        transform: 'translateY(-21px)',
        transition: 'all 0.2s cubic-bezier(0.5, 0, 0, 1.25), opacity 0.15s ease-out',
        zIndex: 1,
        '&:hover': {
            '& $option': {
                '&:not(:hover)': {
                    backgroundColor: 'transparent'
                }
            }
        }
    },
    pushRight: {
        paddingLeft: '35px !important'
    },
    option: {
        ...truncateText('100%'),
        minHeight: 47,
        width: '100%',
        borderBottom: '1px solid #d8d8d8',
        fontFamily: theme.fontFamily,
        fontSize: rem(14),
        fontWeight: 'normal',
        lineHeight: rem(47),
        color: theme.colors.strong,
        cursor: 'pointer',
        outline: 'none',
        opacity: .8,
        transition: 'all 0.2s ease-in-out',
        paddingLeft: 10,
        borderLeft: '1px solid transparent',
        borderRight: '1px solid transparent',
        '&$selected, &:focus, &:hover': {
            backgroundColor: '#f6f6f6',
            borderLeft: '1px solid #d8d8d8',
            borderRight: '1px solid #d8d8d8'
        },
        '&:last-child': {
            borderBottom: 0
        }
    },
    selectedOption: {
        fontWeight: 'bold',
        borderLeft: '1px solid #d8d8d8',
        borderRight: '1px solid #d8d8d8'
    },
    disabledOption: {
        color: '#999',
        backgroundColor: 'transparent',
        cursor: 'default'
    }
});

class Select extends Component {
    static defaultProps = {
        onChange: () => {},
        enabled: true
    };

    static propTypes = {
        onChange: PropTypes.func,
        enabled: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.state = {
            open: false,
            children: [],
            selectedIdx: -1,
            oldSelectedIdx: -1,
            marginBottom: 0,
            selected: false
        };

        this.onChange = this.onChange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleLiClick = this.handleLiClick.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
    }

    get value() {
        const {children, selectedIdx} = this.state;
        const selected = children[selectedIdx];

        if (selected) {
            const {value} = selected.props;
            return value;
        }
    }

    getErrors(label) {
        if (this.value === undefined) {
            return [`${label} is empty`];
        }
        return [];
    }

    handleLiClick(e) {
        const {enabled} = this.props;
        if (!enabled) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const liItem = e.target.closest('li');
        this.setState({
            open: false,
            selectedIdx: liItem.getAttribute('data-idx')
        }, this.onChange);
    }

    onChange() {
        const {children, selectedIdx} = this.state;
        const {onChange} = this.props;
        if (selectedIdx === -1) {
            onChange();
        } else {
            const selected = children[selectedIdx];
            if (selected) {
                const {value, children, ...theRest} = selected.props;
                onChange(value, children, theRest);
            }
        }
    }

    handleClick() {
        const {enabled} = this.props;
        if (!enabled) {
            return;
        }

        if (this.justFocused) {
            delete this.justFocused;
        } else {
            const {open} = this.state;
            this.setState({open: !open});
        }
    }

    handleFocus() {
        const {enabled} = this.props;
        if (!enabled) {
            return;
        }

        this.setState({
            open: true,
            selected: true
        });

        this.justFocused = true;
        this.refs.input.value = '';
        this.handleInputChange();
    }

    handleBlur() {
        this.refs.input.value = '';
        this.setState({selected: false});
        // list needs to close after a delay
        // to enable an item to be selected
        setTimeout(() => {
            this.setState({open: false});
            this.onChange();
        }, 300);
    }

    handleKeyDown(e) {
        const {enabled} = this.props;
        if (!enabled) {
            return;
        }

        const {oldSelectedIdx, open, selectedIdx} = this.state;

        // enter key or tab key
        if (e.keyCode === 13 || e.keyCode === 9) {
            this.refs.input.value = '';

            this.setState({
                open: false,
                oldSelectedIdx: selectedIdx
            }, this.onChange);

            return;
        }

        // escape key
        if (e.keyCode === 27) {
            this.refs.input.value = '';
            this.handleInputChange();

            this.setState({
                open: false,
                selectedIdx: oldSelectedIdx
            }, this.onChange);

            return;
        }

        // up or down keys
        if (e.keyCode === 38 || e.keyCode === 40) {
            e.preventDefault();
            const {selectedIdx, children} = this.state;

            if (e.keyCode === 40 || e.keyCode === 38) {
                if (selectedIdx + 1 < children.length) {
                    this.setState({
                        selectedIdx: selectedIdx + 1
                    });
                }
            }

            // up key
            if (e.keyCode === 38) {
                if (selectedIdx > 0) {
                    this.setState({
                        selectedIdx: selectedIdx - 1
                    });
                }
            }

            // down key
            if (e.keyCode === 40 && !open) {
                this.setState({
                    open: true
                });
            }
        }
    }

    componentDidMount() {
        const {children} = this.props;
        this.setSelected(children);
    }

    componentWillReceiveProps(nextProps) {
        this.setSelected(nextProps.children);
    }

    setSelected(children) {
        children = Children.toArray(children);

        let selectedIdx = -1;
        for (let i = 0; i < children.length; i++) {
            const {selected} = children[i].props;
            if (selected) {
                selectedIdx = i;
                break;
            }
        }

        if (children.length > 0) {
            if (selectedIdx < 0) {
                selectedIdx = 0;
            }
        }

        this.setState({
            children,
            selectedIdx,
            oldSelectedIdx: selectedIdx
        }, this.calculateHeight);
    }

    calculateHeight() {
        const {children} = this.state;

        let foundLabel = false;
        for (let i = 0; i < children.length; i++) {
            if (children[i].props.omitFromList) {
                foundLabel = true;
                break;
            }
        }

        let nChildren = children.length;
        if (foundLabel) nChildren--;

        const marginBottom = nChildren * 47 + 5;
        this.setState({marginBottom});
    }

    handleInputChange() {
        this.filter();
    }

    filter() {
        const {children: propsChildren} = this.props;
        let children = Children.toArray(propsChildren);

        const filter = this.refs.input.value.toLowerCase();
        children = children.filter(child => {
            const res = child.props.children;
            if (typeof res === 'string' || typeof res === 'number') {
                if (res.toString().toLowerCase().startsWith(filter)) {
                    return true;
                } else {
                    return false;
                }
            }
            return true;
        });

        let selectedIdx = -1;
        for (let i = 0; i < children.length; i++) {
            const {selected} = children[i].props;
            if (selected) {
                selectedIdx = i;
                break;
            }
        }

        if (children.length > 0) {
            if (selectedIdx < 0) {
                selectedIdx = 0;
            }
        }

        this.setState({
            children,
            selectedIdx
        }, this.calculateHeight);
    }

    render() {
        const {children, open, selectedIdx, marginBottom, selected} = this.state;
        const {enabled, classes = {}, readOnly, style: incomingStyle} = this.props;
        const cls = classNames(classes.root, classes.wide, {
            [classes.open]: open,
            [classes.disabled]: !enabled,
            [classes.selected]: selected
        });
        const style = {
            ...incomingStyle,
            marginBottom: open ? marginBottom : 0
        };
        const selectedChild = children[selectedIdx];
        const text = selectedChild ? selectedChild.props.children : '';
        const placeholder = selectedChild ? selectedChild.props.omitFromList : false;
        const hideInput = !this.refs.input || !this.refs.input.value;
        const inputCls = classNames(classes.select, {
            [classes.hide]: hideInput
        });

        return (
            <div
                className={cls}
                style={style}
                onClick={this.handleClick}
                onFocus={this.handleFocus}
                onKeyDown={this.handleKeyDown}
                onBlur={this.handleBlur}>
                <span className={classNames('select-input', classes.current, {
                    [classes.placeholder]: placeholder
                })}>{text}</span>
                <input
                    ref="input"
                    type="text"
                    readOnly={!!readOnly}
                    className={inputCls}
                    onChange={this.handleInputChange} />
                {open && (
                    <ul className={classes.list}>
                        {children.map((child, idx) => {
                            const {
                                value,
                                children,
                                omitFromList = false,
                                startAdornment,
                                ...theRest
                            } = child.props;

                            if (!omitFromList) {
                                const cls = classNames(classes.option, {
                                    [classes.selected]: selectedIdx === idx,
                                    [classes.focus]: selectedIdx === idx,
                                    [classes.pushRight]: startAdornment
                                });

                                return (
                                    <Fragment key={idx}>
                                        <li
                                            onClick={this.handleLiClick}
                                            data-value={value}
                                            data-idx={idx}
                                            className={cls}
                                            {...theRest}>
                                            {startAdornment && startAdornment}
                                            {children}
                                        </li>
                                    </Fragment>
                                );
                            }
                        })}
                    </ul>
                )}
            </div>
        );
    }
}

export default injectSheet(styles)(Select);
